import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TableModule } from 'primeng/table';
import { DividerModule } from 'primeng/divider';
import { MessageModule } from 'primeng/message';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { PanelService } from '@cosmos/angular/utils';
import { DatapointAdapter } from '../datapoint.model';

/** One row of the comparison table: a variable, its value here, and the
 *  neighbouring values with their signed delta / %. */
interface CompareRow {
  key: string;
  label: string;
  current: number | null;
  prev: { value: number | null; delta: number | null; pct: number | null } | null;
  next: { value: number | null; delta: number | null; pct: number | null } | null;
}

@Component({
  selector: 'cosmos-datapoint-details',
  imports: [
    DatePipe, DecimalPipe, TranslatePipe,
    CardModule, ButtonModule, TagModule, TableModule, DividerModule, MessageModule, ConfirmDialogModule,
  ],
  templateUrl: './datapoint-details.component.html',
  providers: [ConfirmationService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatapointDetailsComponent {
  private readonly panelService = inject(PanelService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly translateService = inject(TranslateService);

  /** Set by PanelService.initialize(DatapointDetailsComponent, { adapter }). */
  readonly adapter = input.required<DatapointAdapter>();

  /** Called after a successful datapoint removal (measurement component refreshes; we close). */
  readonly onDeleted = input<(() => void) | undefined>(undefined);

  /** Downloads a source CSV by its IngestedCsv id — the owning component wires
   *  this to the ingested-csv API (it holds the measurement id). Absent = no
   *  download button on the source-file card. */
  readonly downloadCsv = input<((ingestedCsvId: string, filename?: string) => void) | undefined>(undefined);

  onDownloadCsv(att: { ingestedCsvId: string; filename?: string }): void {
    this.downloadCsv()?.(att.ingestedCsvId, att.filename);
  }

  readonly view = computed(() => this.adapter().current());
  readonly deleting = signal(false);

  /** Merge highlight-agnostic per-variable rows for the comparison table. */
  readonly compareRows = computed<CompareRow[]>(() => {
    const v = this.view();
    const prevByKey = new Map((v.compare.prev ?? []).map((c) => [c.key, c]));
    const nextByKey = new Map((v.compare.next ?? []).map((c) => [c.key, c]));

    return v.values
      .filter((val) => typeof val.value === 'number')
      .map((val) => {
        const p = prevByKey.get(val.key);
        const n = nextByKey.get(val.key);
        return {
          key: val.key,
          label: val.label,
          current: val.value as number,
          prev: p ? { value: (val.value as number) - p.delta, delta: p.delta, pct: p.pct } : null,
          next: n ? { value: (val.value as number) - n.delta, delta: n.delta, pct: n.pct } : null,
        };
      });
  });

  readonly hasComparison = computed(() => this.compareRows().some((r) => r.prev || r.next));

  next(): void { this.adapter().next(); }
  prev(): void { this.adapter().prev(); }
  close(): void { this.panelService.close(); }

  isNumber(v: unknown): v is number { return typeof v === 'number'; }

  csvStatusSeverity(status?: string): 'success' | 'warn' | 'secondary' {
    switch (status) {
      case 'processed': return 'success';
      case 'orphan': return 'warn';
      default: return 'secondary'; // 'removed' or unknown
    }
  }

  formatDelta(delta: number | null, pct: number | null): string {
    if (delta === null) return '';
    const d = new DecimalPipe('en-US').transform(delta, '1.0-3') ?? String(delta);
    const sign = delta > 0 ? '+' : '';
    const pctStr = pct === null ? '' : ` (${pct > 0 ? '+' : ''}${pct.toFixed(1)}%)`;
    return `${sign}${d}${pctStr}`;
  }

  onDeleteClick(event: Event): void {
    const affected = this.adapter().affectedCount?.() ?? 0;
    this.confirmationService.confirm({
      target: event.currentTarget as EventTarget,
      header: this.translateService.instant('measurement.datapoint.remove.title'),
      message: this.translateService.instant('measurement.datapoint.remove.body', { count: affected }),
      icon: 'fa-solid fa-triangle-exclamation',
      closable: true,
      rejectButtonProps: {
        label: this.translateService.instant('button.cancel'),
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: this.translateService.instant('measurement.datapoint.remove.confirm'),
        severity: 'danger',
      },
      accept: () => {
        this.deleting.set(true);
        this.adapter().delete().subscribe({
          next: () => {
            this.deleting.set(false);
            this.onDeleted()?.();
            this.close();
          },
          error: () => this.deleting.set(false),
        });
      },
    });
  }
}
